import { Capacitor } from '@capacitor/core';

/**
 * Absolute base URL of the public site — the one serving /privacy, /terms and
 * /delete-account.
 *
 * On the WEB it is simply wherever the app is being served from, so the same
 * build is correct on astrally-prod.web.app today and on astrally.in the
 * moment that domain's DNS points at Firebase Hosting. No rebuild, no
 * environment change, no window where the link is wrong.
 *
 * On ANDROID there is nothing to derive from: Capacitor serves the bundle from
 * an internal `https://localhost` origin, so a relative or origin-derived link
 * would resolve inside the WebView and 404. Native therefore reads
 * VITE_PUBLIC_SITE, defaulting to the Firebase domain because that is what
 * actually answers today.
 */
const NATIVE_FALLBACK = 'https://astrally-prod.web.app';

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

export function publicSiteOrigin(): string {
  const configured = import.meta.env.VITE_PUBLIC_SITE?.trim();

  if (Capacitor.isNativePlatform()) {
    return stripTrailingSlash(configured || NATIVE_FALLBACK);
  }

  // Web. An explicit override still wins — useful for a preview deployment
  // that should link at the real published policies rather than its own copy.
  if (configured) return stripTrailingSlash(configured);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // `localhost:5173` during development would give a dead link, so fall back.
  return /^https?:\/\/(localhost|127\.0\.0\.1)/.test(origin) || !origin
    ? NATIVE_FALLBACK
    : stripTrailingSlash(origin);
}

/** Full URL of a published legal page, e.g. legalUrl('privacy'). */
export function legalUrl(page: 'privacy' | 'terms' | 'delete-account'): string {
  return `${publicSiteOrigin()}/${page}`;
}
